// Join requests from people who used an invite code. Approving adds them as a
// Member; declining removes the pending membership.
import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@app/auth/AuthContext';
import { useGroup } from '@admin/shared/contexts/GroupContext';
import { membershipsApi } from '@app/api/organization';
import type { EnrichedMembership, TeamAssignmentResult } from '@app/types';
import { Toast } from '@app/components/Notifications';
import { Avatar, Button, ConfirmDialog, EmptyState, ErrorState, PageHeader, SkeletonRows } from '@app/components/ui';
import { relativeWhen } from '@app/lib/format';
import { emitMembershipChanged, onMembershipChanged } from '@app/utils/membershipEvents';
import { logger } from '@app/utils/logger';

/**
 * The toast after approving. Approval always succeeds; when they asked for a
 * team, the server says whether they were added to it (a full team, or one
 * since deleted, leaves them approved but on no team).
 */
export function approvalMessage(
  name: string,
  result: TeamAssignmentResult | null | undefined,
  fallbackTeam?: string,
): { m: string; t: 'success' | 'error' | 'info' } {
  const team = result?.groupName || fallbackTeam || 'the team they asked for';
  switch (result?.status) {
    case 'assigned':
      return { m: `${name} joined ${team} as a Member`, t: 'success' };
    case 'team_full':
      return { m: `${name} is approved, but ${team} is full. Raise its size limit on Teams, then add them from Members.`, t: 'info' };
    case 'team_unavailable':
      return { m: `${name} is approved. The team they asked for no longer exists, so add them to one from Members.`, t: 'info' };
    case 'failed':
      return { m: `${name} is approved, but couldn’t be added to ${team}. Add them from Members.`, t: 'info' };
    default:
      return { m: `${name} joined as a Member`, t: 'success' };
  }
}

export const Requests: React.FC = () => {
  const { user } = useAuth();
  const { groups } = useGroup();
  const [pending, setPending] = useState<EnrichedMembership[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [decline, setDecline] = useState<EnrichedMembership | null>(null);
  const [toast, setToast] = useState<{ m: string; t: 'success' | 'error' | 'info' } | null>(null);

  const load = useCallback(async () => {
    if (!user?.churchId) return;
    setLoading(true);
    setError(false);
    try {
      setPending(await membershipsApi.getPendingMembers(user.churchId));
    } catch (err) {
      logger.error('Failed to fetch pending memberships:', err);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [user?.churchId]);

  useEffect(() => {
    void load();
  }, [load]);
  // Re-fetch when membership changes happen elsewhere (bell, other tabs).
  useEffect(() => onMembershipChanged(() => void load()), [load]);

  const approve = async (m: EnrichedMembership) => {
    if (!user?.id) return;
    setBusy(m.id);
    try {
      const approved = await membershipsApi.approveMembership(m.id, user.id);
      setPending((prev) => prev.filter((x) => x.id !== m.id));
      emitMembershipChanged();
      setToast(approvalMessage(m.username || m.email, approved?.teamAssignment, teamName(m)));
    } catch (err: any) {
      setToast({ m: err?.response?.data?.error || 'Failed to approve membership', t: 'error' });
    } finally {
      setBusy(null);
    }
  };

  const doDecline = async () => {
    if (!user?.churchId || !decline) return;
    try {
      await membershipsApi.removeFromChurch(user.churchId, decline.userId);
      setPending((prev) => prev.filter((x) => x.id !== decline.id));
      emitMembershipChanged();
    } catch (err: any) {
      setToast({ m: err?.response?.data?.error || 'Failed to decline the request', t: 'error' });
    }
  };

  // A request carries the team from their invite code as pendingGroupId.
  const teamName = (m: EnrichedMembership) => groups.find((g) => g.id === (m.pendingGroupId || m.group?.groupId))?.name;

  return (
    <div className="max-w-[760px]">
      {toast && <Toast message={toast.m} type={toast.t} onClose={() => setToast(null)} />}
      <PageHeader eyebrow={loading ? 'Loading…' : `${pending.length} waiting`} title="Join requests" />
      {error ? (
        <ErrorState title="Couldn't load requests" body="Check your connection and try again." onRetry={load} retrying={loading} />
      ) : loading ? (
        <SkeletonRows rows={3} />
      ) : pending.length === 0 ? (
        <EmptyState title="No requests waiting" body="When someone joins with an invite code, they show up here for you to approve." />
      ) : (
        <ul className="flex flex-col gap-2.5">
          {pending.map((m) => (
            <li key={m.id} className="flex flex-wrap gap-3.5 items-center rounded-[20px] border border-line bg-card p-[18px]">
              <Avatar name={m.username || m.email} size={44} />
              <div className="flex-1 min-w-[200px]">
                <div className="text-[15px] font-semibold">{m.username || m.email}</div>
                <div className="text-[12.5px] text-t4 mt-0.5">
                  {m.email}
                  {m.joinedAt ? ` · ${relativeWhen(m.joinedAt)}` : ''}
                </div>
                <div className="text-[12.5px] text-t3 mt-1">Joins {teamName(m) ?? 'your church'} as a Member</div>
              </div>
              <div className="flex gap-2">
                <Button variant="ghost" onClick={() => setDecline(m)} className="hover:!text-acc">
                  Decline
                </Button>
                <Button variant="dark" onClick={() => approve(m)} loading={busy === m.id}>
                  Approve
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
      <ConfirmDialog
        isOpen={!!decline}
        onClose={() => setDecline(null)}
        onConfirm={doDecline}
        type="danger"
        title={decline ? `Decline ${decline.username || decline.email}?` : 'Decline request?'}
        message="Their request is removed. They can ask again with a new invite code."
        confirmText="Decline"
      />
    </div>
  );
};
